import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Loader2, Pencil, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { PicassoFrame } from '../components/PicassoFrame'
import { TurnView } from '../components/TurnView'
import { MODE_ICON } from '../components/modes'
import { errorMessage, useCreatePicassoSession, usePicassoSession, usePicassoTools, useRenamePicassoSession, useRunTurn } from '../hooks/usePicasso'
import type { PicassoId, Turn, TurnInput, TurnMode } from '../api/picassoApi'

const MODES: Array<{ mode: TurnMode; what: string }> = [
  { mode: 'search', what: 'The storefront search box: graph retrieval plus a written message. Follow-ups in a session refine the last search.' },
  { mode: 'chat', what: 'The conversational concierge agent. The session’s earlier messages go with each new one.' },
  { mode: 'tool', what: 'The MCP tools other agents call (the editorial agent, the film studio). Same arguments, same answers.' },
]

const EXAMPLES = ['calm green painting for a nursery', 'moody coastal landscapes, nothing too dark', 'bold abstract for a big living-room wall', 'gift for someone who loves jazz']

/** What each tool takes; seed handles are filled from the turn on screen when it has results. */
function toolTemplate(tool: string, handles: string[]): Record<string, unknown> {
  const seeds = handles.slice(0, 3)
  switch (tool) {
    case 'search_artworks': return { query: '', limit: 12 }
    case 'ask_concierge': return { query: '', limit: 8 }
    case 'explore_concept': return { concept: '', limit: 12 }
    case 'concept_walk': return { seed_handles: seeds, dimensions: 3, follow_facets: [] }
    case 'recommend_similar': return { seed_handles: seeds, limit: 8 }
    case 'faceted_discovery': return { concept: '', count: 8 }
    case 'get_artwork_facets': return { handles: seeds }
    case 'find_artist_works': return { artist: '', limit: 12 }
    default: return {}
  }
}

function Composer({ onRun, busy, current, initial }: { onRun: (body: TurnInput) => Promise<boolean>; busy: boolean; current: Turn | undefined; initial?: string }) {
  const { data: tools } = usePicassoTools()
  const [mode, setMode] = useState<TurnMode>('search')
  const [text, setText] = useState(initial ?? '')
  const [backend, setBackend] = useState('')
  const [limit, setLimit] = useState(24)
  const [written, setWritten] = useState(true)
  const [fast, setFast] = useState(false)
  const [tool, setTool] = useState('search_artworks')
  const handles = (current?.results ?? []).map((r) => r.handle).filter(Boolean)
  const [args, setArgs] = useState(() => JSON.stringify(toolTemplate('search_artworks', []), null, 2))
  const [argsError, setArgsError] = useState<string | null>(null)

  useEffect(() => {
    setArgs(JSON.stringify(toolTemplate(tool, handles), null, 2))
    setArgsError(null)
    // Re-seed only when the tool changes, not on every new turn (it would wipe edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool])

  const submit = async () => {
    if (busy) return
    if (mode === 'tool') {
      let parsed: Record<string, unknown>
      try {
        parsed = JSON.parse(args)
      } catch (e) {
        setArgsError('The arguments are not valid JSON.')
        return
      }
      await onRun({ mode, tool, args: parsed, input: JSON.stringify(parsed) })
      return
    }
    if (!text.trim()) return
    const submittedText = text.trim()
    const succeeded = await onRun({
      mode,
      input: submittedText,
      options: mode === 'search' ? { backend: backend || undefined, limit, concierge: written, fast } : { limit: Math.min(limit, 24) },
    })
    if (succeeded) setText((draft) => draft.trim() === submittedText ? '' : draft)
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {MODES.map((m) => {
            const Icon = MODE_ICON[m.mode]
            return (
              <button
                key={m.mode}
                onClick={() => setMode(m.mode)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${mode === m.mode ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
              >
                <Icon className="h-3.5 w-3.5" /> {m.mode === 'chat' ? 'Ask the concierge' : m.mode === 'tool' ? 'Agent tools' : 'Search'}
              </button>
            )
          })}
          <span className="text-xs text-slate-500">{MODES.find((m) => m.mode === mode)!.what}</span>
        </div>

        {mode !== 'tool' ? (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                className="h-10 pl-9 text-base"
                placeholder={mode === 'search' ? 'Search like a shopper would — or refine: “bluer”, “more like the third one”…' : 'Ask the concierge anything a shopper might…'}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                autoFocus
              />
            </div>
            <Button className="h-10" disabled={busy || !text.trim()} onClick={submit}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'search' ? 'Search' : 'Send'}
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-[16rem_1fr]">
            <div className="space-y-1">
              {(tools?.data ?? []).map((t) => (
                <button
                  key={t}
                  onClick={() => setTool(t)}
                  className={`block w-full rounded px-2 py-1 text-left font-mono text-xs ${tool === t ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <Textarea className="min-h-[150px] font-mono text-xs" value={args} onChange={(e) => { setArgs(e.target.value); setArgsError(null) }} />
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" disabled={busy} onClick={submit}>
                  {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Call {tool}
                </Button>
                {handles.length > 0 && ['concept_walk', 'recommend_similar', 'get_artwork_facets'].includes(tool) && (
                  <span className="text-xs text-slate-500">Seeded from the top results of the turn on screen.</span>
                )}
                {argsError && <span className="text-xs text-rose-700">{argsError}</span>}
              </div>
            </div>
          </div>
        )}

        {mode === 'search' && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <label className="flex items-center gap-1.5">
              Retriever
              <select className="rounded border bg-white px-1.5 py-0.5" value={backend} onChange={(e) => setBackend(e.target.value)}>
                <option value="">default</option>
                {(tools?.backends ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1.5">
              Results
              <select className="rounded border bg-white px-1.5 py-0.5" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                {[12, 24, 48].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1.5"><Switch checked={written} onCheckedChange={setWritten} /> Written message</label>
            <label className="flex items-center gap-1.5" title="Skip query expansion: the instant lexical + facet path">
              <Switch checked={fast} onCheckedChange={setFast} /> Fast path
            </label>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/** The session's turns, oldest first; the selected one is shown in full. */
function Timeline({ turns, selected, onSelect, pending }: { turns: Turn[]; selected: PicassoId | undefined; onSelect: (id: PicassoId) => void; pending: string | null }) {
  return (
    <div className="space-y-1">
      {turns.map((t, i) => {
        const Icon = MODE_ICON[t.mode]
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            className={`flex w-full items-start gap-2 rounded-md border px-2.5 py-2 text-left text-sm ${selected === t.id ? 'border-slate-900 bg-white' : 'border-transparent hover:bg-white'}`}
          >
            <span className="mt-0.5 w-4 shrink-0 text-right text-[10px] tabular-nums text-slate-400">{i + 1}</span>
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 break-words">{t.mode === 'tool' ? `${t.tool}` : t.input}</span>
              <span className={`block text-[11px] ${t.error ? 'text-rose-700' : 'text-slate-500'}`}>
                {t.error ? 'error' : `${t.results.length} results · ${t.concepts.length} concepts`}
                {t.latencyMs != null && ` · ${(t.latencyMs / 1000).toFixed(1)} s`}
              </span>
            </span>
          </button>
        )
      })}
      {pending && (
        <div className="flex items-start gap-2 rounded-md border border-dashed px-2.5 py-2 text-sm text-slate-500">
          <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
          <span className="line-clamp-2">{pending}</span>
        </div>
      )}
    </div>
  )
}

function Title({ id, title }: { id: PicassoId; title: string }) {
  const rename = useRenamePicassoSession(id)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(title)
  useEffect(() => setValue(title), [title])
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        {title}
        <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-700" title="Rename session"><Pencil className="h-4 w-4" /></button>
      </span>
    )
  }
  return (
    <Input
      autoFocus
      className="h-9 max-w-xl text-lg"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => { setEditing(false); if (value.trim() && value !== title) rename.mutate(value.trim()) }}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  )
}

export function PicassoConsolePage() {
  const { sessionId: raw } = useParams()
  const sessionId = raw || undefined
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data: session, isLoading, error: sessionError, refetch } = usePicassoSession(sessionId)
  const create = useCreatePicassoSession()
  const run = useRunTurn()
  const [pending, setPending] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const turns = session?.turns ?? []
  const selectedId = params.get('turn') || turns[turns.length - 1]?.id
  const current = turns.find((t) => String(t.id) === String(selectedId))

  const go = async (body: TurnInput) => {
    setSubmitError(null)
    setPending(body.mode === 'tool' ? `${body.tool}` : body.input)
    try {
      const id = sessionId ?? (await create.mutateAsync(undefined)).id
      const turn = await run.mutateAsync({ id, body })
      if (!sessionId) navigate(`/app/picasso/s/${id}?turn=${turn.id}`)
      else setParams({ turn: String(turn.id) })
      return true
    } catch (error) {
      setSubmitError(errorMessage(error))
      return false
    } finally {
      setPending(null)
    }
  }
  const busy = Boolean(pending)

  return (
    <PicassoFrame
      title={session ? <Title id={session.id} title={session.title} /> : 'Picasso'}
      description={
        session ? (
          <>{turns.length} turn{turns.length === 1 ? '' : 's'} · each kept with the concierge&apos;s full answer and how it reached it through the graph.</>
        ) : (
          <>
            Search the catalogue the way shoppers and our agents do, through the Picasso concierge, and see how each answer was reached:
            the concepts it matched, the directions it widened into, and which concepts carried each result. Every turn is kept as a
            session you can come back to.
          </>
        )
      }
      actions={session && (
        <Button size="sm" variant="outline" asChild>
          <Link to="/app/picasso"><Plus className="mr-1.5 h-3.5 w-3.5" /> New session</Link>
        </Button>
      )}
    >
      <Composer onRun={go} busy={busy} current={current} initial={params.get('q') ?? undefined} />

      {submitError && <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">Could not complete the request: {submitError}</div>}
      {sessionError && (
        <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Could not load this session: {errorMessage(sessionError)}
          <Button variant="outline" size="sm" className="ml-3" onClick={() => void refetch()}>Try again</Button>
        </div>
      )}

      {sessionId && isLoading && <Skeleton className="h-96 w-full" />}

      {!sessionId && !pending && (
        <Card>
          <CardContent className="space-y-3 p-6">
            <div className="text-sm font-medium">Try a shopper&apos;s question</div>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((q) => (
                <button key={q} onClick={() => go({ mode: 'search', input: q, options: { limit: 24, concierge: true } })} className="rounded-full border bg-white px-3 py-1 text-sm hover:bg-slate-50">
                  {q}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Searches from here reach the live concierge tagged as staff traffic (<code>ams-admin</code>), so they are kept apart from shoppers&apos; in its reports.
              Your past sessions are under <Link to="/app/picasso/sessions" className="underline">Your sessions</Link>.
            </p>
          </CardContent>
        </Card>
      )}

      {(turns.length > 0 || pending) && (
        <div className="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
          <div className="rounded-lg bg-slate-50 p-2 lg:sticky lg:top-4 lg:self-start">
            <div className="px-2 pb-1 text-xs font-medium text-slate-500">This session</div>
            <Timeline turns={turns} selected={current?.id} onSelect={(id) => setParams({ turn: String(id) })} pending={pending} />
          </div>
          <Card className="min-w-0">
            <CardContent className="p-5">
              {current ? (
                <TurnView key={current.id} turn={current} busy={busy} onFollowUp={(q) => go({ mode: 'search', input: q, options: { limit: 24, concierge: true } })} />
              ) : pending ? (
                <div role="status" className="flex items-center gap-2 py-16 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Asking the concierge — a graph search with a written message takes a few seconds…
                </div>
              ) : (
                <div className="space-y-3 py-8 text-sm text-slate-500">
                  <p>This turn is unavailable. Choose another turn from the session.</p>
                  {turns.length > 0 && <Button variant="outline" size="sm" onClick={() => setParams({ turn: String(turns[turns.length - 1].id) })}>Show latest turn</Button>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </PicassoFrame>
  )
}
